import { FloatingPortal } from "@floating-ui/react";
import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  Icon,
  type ListItemProps,
} from "@studio3t/generic-components";
import { useQuery } from "@tanstack/react-query";
import { type CSSProperties, useEffect, useState } from "react";
import styled from "styled-components";
import { testSuitesApi } from "@/api/client";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { queryKeys } from "@/hooks/queryKeys";
import type { ExplorerItem } from "./explorerData";
import {
  parseSuiteRowId,
  TEST_OPERATOR_LABELS,
  type TestAssertionOp,
  usePipelineStaging,
} from "./pipelineStaging";
import {
  HeaderActionSlot,
  PreviewCard,
  PreviewHeader,
  PreviewHeaderButton,
  PreviewScroll,
  PreviewTitle,
} from "./previewCardChrome";
import {
  acquirePreviewSuppression,
  releasePreviewSuppression,
} from "./previewMenuPin";
import { useSuiteCatalog } from "./useTestSuiteSync";

// A test suite row's long-hover card: the Bridge_DocPreview shell around
// the Tests tab's line language — every case of the stored suite, each
// name a heading over its assertions as read-only Bridge_TestLine rows
// (field and operator in textlight, the value's JSON form in
// textheading), like the editor's viewing face (14785-268156). The header
// hosts Delete instead of the code previews' Copy: suites are catalog
// rows, so the card is also where a stored suite is removed — behind a
// confirmation modal. Deleting the suite the panel currently stages also
// unstages that group (for now the tree delete wins over the session
// copies — unstaging alone still never touches the catalog).

// The suite delete flow shared by the hover card's Delete and the tree
// row's right-click menu: the catalog delete lands first; the staged
// group goes with the stored suite (by id — the panel stages exactly one
// suite).
function useSuiteDelete(suiteId: string | null) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { deleteSuite } = useSuiteCatalog();
  const { stagedSuite, unstageSuiteGroup } = usePipelineStaging();

  // While THIS delete's confirm is up, no hover preview may open anywhere
  // in the tree; the guard registers only when it acquired, so other rows
  // unmounting can't release someone else's suppression.
  useEffect(() => {
    if (!confirming) return;
    acquirePreviewSuppression();
    return () => releasePreviewSuppression();
  }, [confirming]);

  const confirmDelete = async () => {
    if (!suiteId) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteSuite(suiteId);
      if (stagedSuite?.suiteId === suiteId) unstageSuiteGroup();
      setConfirming(false);
    } catch {
      setError("Failed to delete test suite");
    } finally {
      setDeleting(false);
    }
  };

  return {
    confirming,
    deleting,
    error,
    request: () => {
      setError(null);
      setConfirming(true);
    },
    cancel: () => setConfirming(false),
    confirmDelete,
  };
}

// The delete confirmation, portaled to the body (see TestPreviewHover's
// note on the explorer's stacking contexts).
function SuiteDeleteModal({
  name,
  flow,
}: {
  name: string;
  flow: ReturnType<typeof useSuiteDelete>;
}) {
  return (
    <FloatingPortal>
      <ConfirmDeleteModal
        isOpen={flow.confirming}
        title="Delete test suite"
        message={
          <>
            Are you sure you want to delete <strong>{name}</strong>? This action
            cannot be undone.
          </>
        }
        loading={flow.deleting}
        error={flow.error}
        onConfirm={flow.confirmDelete}
        onCancel={flow.cancel}
      />
    </FloatingPortal>
  );
}

/** Right-click menu for a tree suite row (GenericDropdown's context-menu
 *  mode over the row element): one Delete entry opening the same
 *  confirm-gated catalog delete as the hover card. */
export function SuiteRowContextMenu({
  areaRef,
  suiteId,
  name,
}: {
  areaRef: React.RefObject<HTMLDivElement>;
  suiteId: string;
  name: string;
}) {
  const flow = useSuiteDelete(suiteId);
  const items: ListItemProps[][] = [
    [
      {
        id: "delete-suite",
        type: "text",
        value: "delete-suite",
        label: "Delete",
        closeOnSelect: true,
        onClick: flow.request,
      },
    ],
  ];
  return (
    <>
      <GenericDropdown
        asContextMenu={areaRef}
        dropdownItems={items}
        width={{ strategy: "fixed", size: 160 }}
      />
      <SuiteDeleteModal name={name} flow={flow} />
    </>
  );
}

/** The hover-preview unit for one test suite row: the floating card while
 *  the hover holds it open, plus the delete-confirmation modal — mounted
 *  outside the card so it survives the card closing under the backdrop.
 *  The modal ALSO portals to the body (FloatingPortal): rendered in place
 *  it would sit inside the explorer tree's OverlayScrollbars wrappers,
 *  whose z-index:0/isolation stacking contexts trap the fixed backdrop
 *  under the workbench panels — visually buried and unclickable. */
export function TestPreviewHover({
  item,
  open,
  setFloating,
  style,
  floatingProps,
  closeCard,
}: {
  item: ExplorerItem;
  open: boolean;
  setFloating: (node: HTMLElement | null) => void;
  style: CSSProperties;
  floatingProps: Record<string, unknown>;
  closeCard: () => void;
}) {
  const suiteId = parseSuiteRowId(item.id);
  const flow = useSuiteDelete(suiteId);
  if (!suiteId) return null;

  return (
    <>
      {open && (
        <TestPreviewCard
          name={item.name}
          suiteId={suiteId}
          setFloating={setFloating}
          style={style}
          floatingProps={floatingProps}
          onDelete={() => {
            closeCard();
            flow.request();
          }}
        />
      )}
      <SuiteDeleteModal name={item.name} flow={flow} />
    </>
  );
}

// The floating card itself — rendered only while open. The suite loads
// on demand into the shared query entry (queryKeys.testSuite), so a
// revisit paints from cache; until the suite has landed nothing shows,
// like the code previews.
function TestPreviewCard({
  name,
  suiteId,
  setFloating,
  style,
  floatingProps,
  onDelete,
}: {
  name: string;
  suiteId: string;
  setFloating: (node: HTMLElement | null) => void;
  style: CSSProperties;
  floatingProps: Record<string, unknown>;
  onDelete: () => void;
}) {
  const { data: suite } = useQuery({
    queryKey: queryKeys.testSuite(suiteId),
    queryFn: () => testSuitesApi.get(suiteId),
    // Same freshness window as the code previews' content query.
    staleTime: 15_000,
  });
  if (!suite) return null;
  return (
    <FloatingPortal>
      <PreviewCard
        ref={setFloating}
        style={style}
        data-testid="test-preview-card"
        {...floatingProps}
      >
        <PreviewHeader>
          <PreviewTitle>PREVIEW</PreviewTitle>
          <HeaderActionSlot>
            <PreviewHeaderButton
              icon={Icon.Bin}
              label={`Delete ${name}`}
              onClick={onDelete}
            />
          </HeaderActionSlot>
        </PreviewHeader>
        <PreviewScroll>
          <AssertionList>
            {suite.cases.length === 0 && <EmptyNote>No tests</EmptyNote>}
            {suite.cases.map((suiteCase, caseIndex) => (
              <CaseBlock key={`${caseIndex}-${suiteCase.name}`}>
                <CaseName>{suiteCase.name}</CaseName>
                {(suiteCase.assertions ?? []).length === 0 && (
                  <EmptyNote>No assertions</EmptyNote>
                )}
                {(suiteCase.assertions ?? []).map((a, index) => (
                  <AssertionLine key={`${index}-${a.path}`}>
                    <Dim>{a.path}</Dim>
                    <Dim>
                      {TEST_OPERATOR_LABELS[a.assert_type as TestAssertionOp] ??
                        a.assert_type}
                    </Dim>
                    <Value>{JSON.stringify(a.expected_value) ?? ""}</Value>
                  </AssertionLine>
                ))}
              </CaseBlock>
            ))}
          </AssertionList>
        </PreviewScroll>
      </PreviewCard>
    </FloatingPortal>
  );
}

// max-content width lets long lines extend the scroll region (the card
// scrolls both axes, like the code previews' unwrapped lines).
const AssertionList = styled.div`
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: 100%;
  /* No top padding — the first subheader band sits flush under the
     header's hairline. */
  padding: 0 0 ${designVariables.ui.semantic.spacings}px;
`;

const CaseBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

// The header strip's wash is neutrals/alpha/b2 (2% black) — the scale's
// lightest step, so the subheaders' lighter wash is a raw value below it.
const SUBHEADER_WASH = "rgba(0, 0, 0, 0.018)";

// The suite's case names as SUBHEADERS, Bridge_TestLine height: a wash
// lighter than the PREVIEW header's, hairlines below (always) and above
// (only when another case's assertions run into it — never on the first).
const CaseName = styled.div`
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 ${designVariables.ui.semantic.spacings}px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  color: ${(p) => p.theme.semanticTextHeading};
  background: ${SUBHEADER_WASH};
  border-bottom: 1px solid ${(p) => p.theme.semanticBorderLight};

  ${CaseBlock}:not(:first-child) & {
    border-top: 1px solid ${(p) => p.theme.semanticBorderLight};
  }
`;

// The editor viewer's Bridge_TestLine geometry, minus the card background
// (the preview card already is a card).
const AssertionLine = styled.div`
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.semantic.spacings}px;
  padding: 0 ${designVariables.ui.semantic.spacings}px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
`;

const Dim = styled.span`
  flex-shrink: 0;
  color: ${(p) => p.theme.semanticTextLight};
`;

const Value = styled.span`
  color: ${(p) => p.theme.semanticTextHeading};
`;

const EmptyNote = styled.div`
  padding: 0 ${designVariables.ui.semantic.spacings}px;
  font-size: 14px;
  font-style: italic;
  line-height: 36px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;
